import { useState, useRef, useEffect, ChangeEventHandler, FocusEventHandler } from 'react';
import {
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { motion } from 'framer-motion';

import { styles } from '../styles';
import { useRouter } from 'next/router';
import { palette } from '@/theme/constants';

const suggestionsList = ['Apple', 'Banana', 'Orange', 'Grapes', 'Pineapple', 'Mango', 'Strawberry'];

const Search = ({
  color,
  value,
  onChange,
  onClear,
  sx,
}: {
  color?: any;
  value?: string;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  onClear?: () => void;
  sx?: SxProps<Theme> | undefined;
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        if (isMobile) {
          setIsExpanded(false);
        }
        if (!isMobile && !value) {
          setIsExpanded(false);
        }
        setFilteredSuggestions([]);
        if (isFullScreen) {
          setIsFullScreen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFullScreen, value]);

  const handleInputChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    onChange && onChange(event);
    const inputValue = event.target.value.toLowerCase();
    if (inputValue) {
    } else {
    }
  };
  const isStoryRoute = router.pathname === '/app/story/[id]' || router.pathname === '/app/story/[id]/memory/create';

  const iconSrc = isStoryRoute ? '/icons/search.svg' : '/icons/search-black.svg';

  const handleSuggestionClick = (suggestion: string) => {
    onChange?.({ target: { value: suggestion } } as React.ChangeEvent<HTMLInputElement>);
    setFilteredSuggestions([]);
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = () => {
    if (isMobile && !isFullScreen) {
      setIsFullScreen(true);
    }
  };
  const colorS = router.pathname === '/app/story/[id]' ? '#343434' : '#212d6d';
  const setFoucus = (flag: any) => {
    if (isMobile && !isFullScreen) {
      setIsFullScreen(flag);
    }
  };
  useEffect(() => {
    if (!isMobile && value) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [value, isMobile]);
  useEffect(() => {
    if ((isExpanded || isFullScreen) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded, isFullScreen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') {
    setIsFullScreen(false); // Close modal
    setIsExpanded(false);   // Optionally collapse
    setFilteredSuggestions([]); // Clear suggestions
  }
};

  return (
    <div
      ref={searchRef}
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', position: 'relative', width: '100%' }}>
      {/* Search Icon (Only visible when collapsed) */}
      {!isExpanded && (
        <Box
          onClick={() => {
            setIsExpanded(true);
            setFoucus(true);
          }}
          sx={{ cursor: 'pointer', marginTop: '3px', marginRight: '10px' }}>
          <Image priority src={iconSrc} alt='icon' width={18} height={18} />
        </Box>
      )}

      <TextField
        id='search'
        name='search'
        focused
        inputRef={inputRef}
        
        sx={{
          ...sx,
          ...styles(colorS).inputMobile,
          width: !isMobile && (isExpanded || value) ? '300px' : '0px',
          opacity: !isMobile && (isExpanded || value) ? 1 : 0,
          transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out',
          overflow: 'hidden',
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'transparent !important' },
            '&:hover fieldset': { borderColor: 'transparent !important' },
            '&.Mui-focused fieldset': { borderColor: 'transparent !important' },
          },
        }}
        fullWidth
        onChange={handleInputChange}
        value={value}
        onFocus={handleFocus}
        placeholder={t('search')}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start' disablePointerEvents>
              <IconButton disableTouchRipple disableRipple edge='end'>
                <Image priority src='/icons/search.svg' alt='icon' width={22} height={22} />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment:
            value && value?.length > 0 && onClear ? (
              <InputAdornment position='start' onClick={onClear}>
                <IconButton disableTouchRipple disableRipple edge='end'>
                  <Image priority src='/icons/close-circle-white.svg' alt='icon' width={18} height={18} />
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
      />

      {isMobile && isFullScreen ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#212d6d',
            color: 'white',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column',
            padding: '10px 20px',
          }}>
          {/* Back Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => {
                setIsExpanded(false);
                setIsFullScreen(false);
              }}
              sx={{ color: 'white', mr: 2 }}>
              <Image priority src='/icons/left-arrow.svg' alt='back' width={24} height={24} />
            </IconButton>

            {/* Search Input Field (Centered in Header) */}
            <TextField
              id='search'
              name='search'
              focused
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'transparent !important' },
                  '&:hover fieldset': { borderColor: 'transparent !important' },
                  '&.Mui-focused fieldset': { borderColor: 'transparent !important' },
                },
              }}
              onChange={handleInputChange}
              value={value}
              placeholder={t('search')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <IconButton disableTouchRipple disableRipple>
                      <Image priority src='/icons/search.svg' alt='icon' width={22} height={22} />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment:
                  value && value.length > 0 && onClear ? (
                    <InputAdornment position='start' onClick={onClear}>
                      <IconButton disableTouchRipple disableRipple>
                        <Image priority src='/icons/close-circle-white.svg' alt='icon' width={18} height={18} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
              }}
            />
          </Box>

          {/* Suggestions List */}
          <List sx={{ mt: 2 }}>
            {filteredSuggestions.map((suggestion, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton onClick={() => handleSuggestionClick(suggestion)}>
                  <ListItemText primary={suggestion} sx={{ textAlign: 'left', color: 'white' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </motion.div>
      ) : (
        filteredSuggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '345px',
              backgroundColor: '#212d6d',
              color: 'white',
              borderRadius: '5px',
              boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
              zIndex: 1000,
              marginTop: '5px',
            }}>
            <List>
              {filteredSuggestions.map((suggestion, index) => (
                <ListItem key={index} disablePadding sx={{ justifyContent: 'center' }}>
                  <Box
                    sx={{
                      width: '95%',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      textAlign: 'center',
                    }}>
                    <ListItemButton onClick={() => handleSuggestionClick(suggestion)}>
                      <ListItemText primary={suggestion} />
                    </ListItemButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          </motion.div>
        )
      )}
    </div>
  );
};

export default Search;
